import React, {useRef} from "react";
import "react-datepicker/dist/react-datepicker.css";
import {useDispatch, useSelector} from "react-redux";
import {importListing} from "../services/api";
import {setRefetch} from "../store/reducers/listingReducer";
import {ReactComponent as Upload} from "../assets/svg/upload.svg";
import {ReactComponent as CSV} from "../assets/svg/csv.svg";
import {ReactComponent as AlertCircle} from "../assets/svg/alert-circle.svg";
import {ReactComponent as CheckCircle} from "../assets/svg/check-circle.svg";
import {ReactComponent as CloseIcon} from "../assets/svg/x-close.svg";
import * as Dialog from "@radix-ui/react-dialog";
import {AxiosProgressEvent} from "axios";

interface IProps {
}

export const ImportListing = (props: IProps) => {
    const dispatch = useDispatch();

    const inputRef = useRef<HTMLInputElement>(null);

    const [show, setShow] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>("");
    const [file, setFile] = React.useState<any>(null);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = React.useState(0);

    const logged = useSelector((state: any) => state.listingReducer.logged);
    const userType = useSelector((state: any) => state.listingReducer.userType);

    const handleClose = () => {
        setLoading(false);
        setShow(false);
    };
    const handleShow = () => setShow(true);

    const onFileChange = async (event: any) => {
        const uploadFile = event.target.files[0];
        setFile(uploadFile);

        try {
            const formData = new FormData();
            formData.append("csv", uploadFile);
            setLoading(true);
            await importListing(formData, onUploadProgress);
        } catch (error) {
            setErrorMessage("Import failed");
        } finally {
            setLoading(false);
        }
    };

    const onUploadProgress = (event: AxiosProgressEvent) => {
        const {loaded, total} = event;
        if (!total) return;
        const precent = Math.floor((loaded * 100) / total);
        setUploadProgress(precent);
    };

    async function importListingData() {
        try {
            dispatch(setRefetch(true));
            setShow(false);
        } catch (e) {
            setErrorMessage("Import failed");
            setLoading(false);
        }
    }

    const uploadComplete = file && !loading;

    return (
        <Dialog.Root open={show} onOpenChange={setShow}>
            <Dialog.Trigger>
                <span className="hidden md:flex items-center gap-x-1">
                    <span>
                        <Upload/>
                    </span>
                    <span>Import Listing</span>
                </span>

                <span className="flex md:hidden items-center text-gray-700 gap-x-3 font-semibold px-3 py-2">
                    <span>
                        <Upload/>
                    </span>
                    <span>Import Listing</span>
                </span>
            </Dialog.Trigger>
            <Dialog.Portal>
                <Dialog.Overlay className="bg-gray-800 bg-opacity-60 fixed inset-0 z-30"/>
                <Dialog.Content
                    style={{transform: "translate(-50%, -50%)"}}
                    className="fixed left-1/2 top-1/2 bg-white p-6 z-40 min-w-[26.5rem]"
                >
                    <Dialog.Title className="text-gray-900 text-lg font-semibold">
                        Import Listing
                    </Dialog.Title>

                    <Dialog.Close>
                        <span className="absolute top-6 right-6">
                            <CloseIcon/>
                        </span>
                    </Dialog.Close>

                    <fieldset className="mt-6">
                        <label htmlFor="listing" className="w-full">
                            {file !== null ? (
                                <div
                                    className={`flex items-center justify-between p-4 ${
                                        uploadComplete && errorMessage
                                            ? "border-2 border-red-600"
                                            : ""
                                    } ${
                                        uploadComplete && !errorMessage
                                            ? "border-2 border-green-600"
                                            : ""
                                    }`}
                                >
                                    <div>
                                        <CSV/>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-700">
                                            {file?.name || "test.csv"}
                                        </h4>
                                        {errorMessage ? (
                                            <p>Upload failed, please try again</p>
                                        ) : (
                                            <p className=" text-gray-600">
                                                {file?.size / 1000 || "0"} KB - {uploadProgress}%
                                                uploaded
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        {loading ? (
                                            <CircularProgress progress={uploadProgress}/>
                                        ) : null}

                                        {!loading && uploadProgress ? (
                                            errorMessage ? (
                                                <AlertCircle/>
                                            ) : (
                                                <CheckCircle/>
                                            )
                                        ) : null}
                                    </div>
                                </div>
                            ) : (
                                <button
                                    onClick={() => inputRef.current?.click()}
                                    className="w-full border flex flex-col items-center justify-center py-4"
                                >
                                    <span className="text-primary text-sm font-semibold">
                                        Click to upload
                                    </span>
                                    <span>.CSV File</span>
                                </button>
                            )}

                            <input
                                ref={inputRef}
                                type="file"
                                accept=".csv"
                                className="hidden"
                                name="listing"
                                id="listing"
                                onChange={onFileChange}
                            />
                        </label>
                    </fieldset>

                    <div className="mt-4 flex justify-between items-center">
                        <a
                            className="text-primary text-xs font-medium"
                            href="https://knockit.io/assets/csv/sample.csv"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Download Sample File
                        </a>

                        {uploadComplete ? (
                            <button
                                onClick={() => setFile(null)}
                                className="text-xs font-medium text-red-600"
                            >
                                Delete
                            </button>
                        ) : null}
                    </div>

                    <div className="mt-6 flex gap-x-3 font-semibold">
                        <Dialog.Close className="w-full">
                            <span className="text-gray-700 border py-3 w-full">
                                Close
                            </span>
                        </Dialog.Close>
                        <button
                            onClick={importListingData}
                            disabled={file === null || Boolean(errorMessage) || loading}
                            className={`py-3 ${
                                file === null || Boolean(errorMessage) || loading
                                    ? "bg-gray-200 text-gray-400"
                                    : "text-white bg-primary"
                            } w-full`}
                        >
                            Import Listing
                        </button>
                    </div>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );

    // return (
    //     <>

    //         {/* <a onClick={handleShow} href="#"
    //            className="btn btn-primary btn-small btn-rounded icon shadow import-listing">
    //             <FontAwesomeIcon icon={faFileImport}/><span> Import listing</span></a> */}
    //         <Modal show={true} onHide={handleClose} centered>
    //             <Modal.Header closeButton>
    //                 <Modal.Title>Import Listing</Modal.Title>
    //             </Modal.Header>
    //             <Form onSubmit={(event) => {
    //                 event.preventDefault();
    //                 importListingData()
    //             }}>
    //                 <Modal.Body>

    //                     {errorMessage !== '' &&
    //                         <Alert key={'danger'} variant={'danger'}>
    //                             {errorMessage}
    //                         </Alert>
    //                     }
    //                     <Form.Group
    //                         className="mb-3"
    //                         controlId="importListing.CSV"
    //                     >
    //                         <Form.Label>CSV<sup>*</sup></Form.Label>
    //                         <Form.Control type="file" required={true} accept=".csv" onChange={onFileChange}/>
    //                     </Form.Group>
    //                     <a href={"https://knockit.io/assets/csv/sample.csv"} className={"mt-1"}>Download sample</a>
    //                 </Modal.Body>
    //                 <Modal.Footer>
    //                     {loading &&
    //                         <Spinner animation="border" role="status">
    //                             <span className="visually-hidden">Loading...</span>
    //                         </Spinner>
    //                     }
    //                     <Button variant="secondary" onClick={handleClose}>
    //                         Close
    //                     </Button>
    //                     <Button variant="primary" type={'submit'} onClick={(event) => {
    //                         event.preventDefault();
    //                         importListingData()
    //                     }}>
    //                         Import
    //                     </Button>
    //                 </Modal.Footer>
    //             </Form>
    //         </Modal>
    //     </>

    // );
};

const CircularProgress = ({progress}: { progress: number }) => {
    const curcumference = 87.96;
    const fillPart = (progress * curcumference) / 100;
    const emptyPart = curcumference - fillPart;
    return (
        <svg width="32" height="32" viewBox="0 0 32 32">
            <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke="#F2F4F7"
                stroke-width="4"
                strokeLinecap="round"
                strokeLinejoin="round"
            ></circle>
            <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke="#155EEF"
                stroke-width="4"
                strokeDasharray={`${fillPart} ${emptyPart}`}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                    transform: "rotate(-90deg)",
                    transformOrigin: "16px 16px",
                    transition: "stroke-dasharray 0.3s linear 0s",
                }}
            ></circle>
        </svg>
    );
};
export default ImportListing;
